<template>
  <div class="global-container">
    <div class="content">
      <!-- Header -->
      <div class="header-section">
        <div class="title-section">
          <h1 class="page-title">Payments</h1>
          <p class="page-description">Track and manage company payments</p>
        </div>
        <button class="add-button" @click="openPaymentModal">
          <span class="plus-icon">+</span>
          Add Payment
        </button>
      </div>

      <!-- Stat Cards -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-header">
            <span class="stat-label">Total Payments</span>
            <span class="stat-icon">&#x1F4B3;</span>
          </div>
          <div class="stat-value paid">${{ totalPaymentsAmount.toLocaleString() }}</div>
          <div class="stat-subtext">{{ payments.length }} payments</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs-section">
        <button
          v-for="tab in tabs"
          :key="tab.route"
          :class="['tab-btn', { active: activeTab === tab.name }]"
          @click="navigateToTab(tab.route)"
        >
          {{ tab.name }}
        </button>
      </div>

      <!-- Search and Filters -->
      <div class="search-filter-section">
        <div class="search-bar">
          <span class="search-icon">&#x1F50D;</span>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search payments..."
            class="search-input"
            @input="handleSearch"
          />
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading payments...</p>
      </div>

      <!-- Payments Table -->
      <div v-else class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Payment Date</th>
              <th>Vendor</th>
              <th>Paid Amount</th>
              <th>WHT Deduction</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="payments.length === 0">
              <td colspan="6" class="empty-state">No payments found. Click "Add Payment" to record one.</td>
            </tr>
            <tr v-for="payment in payments" :key="payment.id" class="data-row">
              <td class="date-col">{{ formatDate(payment.paymentDate) }}</td>
              <td class="vendor-col">{{ payment.vendorName || payment.vendor || '-' }}</td>
              <td class="amount-col">${{ payment.netAmount }}</td>
              <td class="amount-col">${{ (payment.whtDeduction || 0).toLocaleString() }}</td>
              <td>{{ payment.notes || '-' }}</td>
              <td class="actions-col">
                <button class="action-btn edit-btn" title="Edit" @click="openEditModal(payment)">&#x270F;&#xFE0F;</button>
                <button class="action-btn delete-btn" title="Delete" @click="confirmDelete(payment)">&#x1F5D1;&#xFE0F;</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Add/Edit Payment Modal -->
    <AddPaymentModal
      :isOpen="isPaymentModalOpen"
      :payment="paymentToEdit"
      @close="closePaymentModal"
      @create="handleCreatePayment"
      @update="handleUpdatePayment"
    />

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="cancelDelete">
      <div class="confirm-modal">
        <h3>Delete Payment</h3>
        <p>Are you sure you want to delete this payment?</p>
        <div class="confirm-actions">
          <button class="btn-cancel" @click="cancelDelete">Cancel</button>
          <button class="btn-delete" @click="handleDelete" :disabled="loading">
            {{ loading ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AddPaymentModal from '@/components/AddPaymentModal.vue'

export default {
  name: 'Payments',
  components: {
    AddPaymentModal
  },
  data() {
    return {
      searchQuery: '',
      isPaymentModalOpen: false,
      paymentToEdit: null,
      showDeleteConfirm: false,
      paymentToDelete: null,
      searchTimeout: null,
      tabs: [
        { name: 'Expenses', route: '/expenses' },
        { name: 'Payments', route: '/payments' },
        { name: 'Vendors', route: '/vendors' },
        { name: 'Categories', route: '/categories' }
      ]
    }
  },
  computed: {
    ...mapGetters('payment', ['payments', 'loading']),
    activeTab() {
      const routeToTab = {
        '/expenses': 'Expenses',
        '/payments': 'Payments',
        '/vendors': 'Vendors',
        '/categories': 'Categories'
      }
      return routeToTab[this.$route.path] || 'Payments'
    },
    totalPaymentsAmount() {
      return this.payments.reduce((sum, p) => sum + (p.totalAmount || p.amount || 0), 0)
    }
  },
  methods: {
    ...mapActions('payment', ['fetchPayments', 'createPayment', 'updatePayment', 'deletePayment']),

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },

    async loadData() {
      const params = {}
      if (this.searchQuery) {
        params.search = this.searchQuery
      }
      await this.fetchPayments(params)
    },

    handleSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadData()
      }, 300)
    },

    openPaymentModal() {
      this.paymentToEdit = null
      this.isPaymentModalOpen = true
    },

    openEditModal(payment) {
      this.paymentToEdit = payment
      this.isPaymentModalOpen = true
    },

    closePaymentModal() {
      this.isPaymentModalOpen = false
      this.paymentToEdit = null
    },

    async handleCreatePayment(paymentData) {
      try {
        await this.createPayment(paymentData)
        this.closePaymentModal()
      } catch (error) {
        console.error('Create payment failed:', error)
      }
    },

    async handleUpdatePayment({ id, payload }) {
      try {
        await this.updatePayment({ id, payload })
        this.closePaymentModal()
      } catch (error) {
        console.error('Update payment failed:', error)
      }
    },

    confirmDelete(payment) {
      this.paymentToDelete = payment
      this.showDeleteConfirm = true
    },

    cancelDelete() {
      this.paymentToDelete = null
      this.showDeleteConfirm = false
    },

    async handleDelete() {
      if (!this.paymentToDelete) return
      try {
        await this.deletePayment(this.paymentToDelete.id)
        this.cancelDelete()
      } catch (error) {
        console.error('Delete failed:', error)
      }
    },

    navigateToTab(route) {
      this.$router.push(route)
    }
  },
  created() {
    this.loadData()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout)
  }
}
</script>

<style scoped>
.header-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 32px;
}

.title-section {
  flex: 1;
}

.page-title {
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.page-description {
  font-size: 15px;
  color: #9ca3af;
  font-weight: 400;
}

.add-button {
  background: #2563eb;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.add-button:hover {
  background: #1d4ed8;
}

.plus-icon {
  font-size: 18px;
}

/* Stat Cards */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

.stat-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.stat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.stat-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.stat-icon {
  font-size: 20px;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;
}

.stat-value.paid {
  color: #10b981;
}

.stat-subtext {
  font-size: 13px;
  color: #9ca3af;
}

/* Tabs */
.tabs-section {
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 32px;
  padding-bottom: 0;
}

.tab-btn {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  padding: 12px 0;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
}

.tab-btn.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
}

.tab-btn:hover {
  color: #1f2937;
}

/* Search and Filters */
.search-filter-section {
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  align-items: center;
}

.search-bar {
  flex: 1;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  gap: 12px;
}

.search-icon {
  color: #9ca3af;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #1f2937;
}

.search-input::placeholder {
  color: #d1d5db;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  margin-top: 16px;
  color: #6b7280;
}

/* Empty State */
.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
}

/* Table */
.table-container {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.data-table th {
  padding: 14px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.5px;
}

.data-row {
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s ease;
}

.data-row:hover {
  background: #f3f4f6;
}

.data-table td {
  padding: 14px 16px;
  font-size: 13px;
  color: #1f2937;
}

.vendor-col {
  color: #6b7280;
}

.date-col {
  color: #6b7280;
  font-size: 12px;
}

.amount-col {
  font-weight: 600;
  color: #1f2937;
}

.actions-col {
  display: flex;
  gap: 8px;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  opacity: 0.6;
  transition: opacity 0.2s ease;
  padding: 4px 8px;
}

.action-btn:hover {
  opacity: 1;
}

.edit-btn:hover {
  color: #2563eb;
}

.delete-btn:hover {
  color: #ef4444;
}

/* Delete Confirmation Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.confirm-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
}

.confirm-modal h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
}

.confirm-modal p {
  color: #6b7280;
  margin-bottom: 24px;
}

.confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-cancel {
  padding: 10px 24px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #1f2937;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-cancel:hover {
  background: #f3f4f6;
}

.btn-delete {
  padding: 10px 24px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-delete:hover {
  background: #dc2626;
}

.btn-delete:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    gap: 16px;
  }

  .search-filter-section {
    flex-direction: column;
  }

  .search-bar {
    width: 100%;
  }
}
</style>
